import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { Box, Collapse, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { BADGE, FONT_SIZE, INK, MOTION, NEUTRAL, OUTREACH_POPUP } from "@/theme/tokens";
import SystemButton from "@/ui/SystemButton/SystemButton";
import { OutreachMatchMeter } from "./OutreachMatchMeter";
import { OutreachRowBadge } from "./OutreachRowBadge";
import { OutreachWhyChips } from "./OutreachWhyChips";
import { OutreachRowAction, type OutreachRowView } from "./outreachGroupTypes";
import { ONE_LINE, TEXT_INSET } from "./outreachRowLayout";
import type { OutreachCopy } from "./useOutreachCopy";

type Props = {
	row: OutreachRowView;
	copy: OutreachCopy;
	/** Manual sends are off unless outreach is running. */
	sendsDisabled: boolean;
	calling: boolean;
	whyOpen: boolean;
	onToggleWhy: () => void;
	onAction: (action: OutreachRowAction, nurseId: number) => void;
	/** Unique per group: the same person can sit in a text batch and on the call list at once. */
	dataTestId: string;
};

const ACTION_LABEL: Record<OutreachRowAction, string> = {
	[OutreachRowAction.SendNow]: "request.outreach.action.text_now",
	[OutreachRowAction.CallNow]: "request.outreach.action.call_now",
	[OutreachRowAction.OfferNow]: "request.outreach.action.text_now",
	[OutreachRowAction.Skip]: "request.outreach.action.skip",
	[OutreachRowAction.Undo]: "request.outreach.action.undo",
	[OutreachRowAction.TextAnyway]: "request.outreach.action.text_anyway",
};

const SENDS: ReadonlySet<OutreachRowAction> = new Set([
	OutreachRowAction.SendNow,
	OutreachRowAction.CallNow,
	OutreachRowAction.OfferNow,
	OutreachRowAction.TextAnyway,
]);
/** Sends that go out the way outreach reaches people right now: a call while calling, else a text. */
const FOLLOWS_CHANNEL: ReadonlySet<OutreachRowAction> = new Set([OutreachRowAction.SendNow, OutreachRowAction.OfferNow]);

/**
 * One person on one line, under a hairline: name, then their match (scored) or why they're left out, then badges,
 * actions and Why; Why folds the factor chips open underneath.
 */
export const OutreachPersonRow: FC<Props> = ({ row, copy, sendsDisabled, calling, whyOpen, onToggleWhy, onAction, dataTestId: testId }) => {
	const { t } = useTranslation();
	const keyOf = (action: OutreachRowAction) => (FOLLOWS_CHANNEL.has(action) && calling ? "request.outreach.action.call_now" : ACTION_LABEL[action]);
	const { scored } = row;

	return (
		<Box sx={{ mx: 1.75, borderTop: `1px solid ${NEUTRAL[150]}` }}>
			<Box
				data-testid={testId}
				sx={{ display: "flex", alignItems: "center", gap: 1.25, minHeight: OUTREACH_POPUP.ROW_MIN_HEIGHT, ps: TEXT_INSET, pe: 0.5, py: 0.75 }}
			>
				<Typography
					component="span"
					sx={{ ...ONE_LINE, width: OUTREACH_POPUP.ROW_NAME_WIDTH, flexShrink: 0, fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, color: INK.PRIMARY }}
				>
					{row.name}
				</Typography>
				{scored === null ? (
					<Typography component="span" sx={{ ...ONE_LINE, flex: 1, minWidth: 0, fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY }}>
						{row.line}
					</Typography>
				) : (
					<>
						<OutreachMatchMeter person={scored} copy={copy} dataTestId={testId} />
						<Box sx={{ flex: 1 }} />
					</>
				)}
				{row.when !== null && (
					<Typography
						component="span"
						data-testid={`${testId}-when`}
						sx={{
							width: OUTREACH_POPUP.ROW_WHEN_WIDTH,
							flexShrink: 0,
							fontSize: FONT_SIZE.CAPTION,
							fontWeight: 600,
							color: INK.SECONDARY,
							fontVariantNumeric: "tabular-nums",
							whiteSpace: "nowrap",
						}}
					>
						{row.when}
					</Typography>
				)}
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
					{row.badges.map((badge) => (
						<OutreachRowBadge key={badge.id} badge={badge} dataTestId={`${testId}-badge-${badge.id}`} />
					))}
					{row.actions.map((action) => (
						<SystemButton
							key={action}
							data-testid={`${testId}-${action}`}
							size="compact"
							buttonVariant={SENDS.has(action) ? "hairline" : "borderless"}
							disabled={SENDS.has(action) && sendsDisabled}
							noFullWidthMobile
							// Every row has the same buttons; say whose they are.
							aria-label={t(`${keyOf(action)}_aria`, { name: row.name })}
							onClick={() => onAction(action, row.nurseId)}
						>
							{t(keyOf(action))}
						</SystemButton>
					))}
				</Box>
				{scored !== null && (
					<SystemButton
						data-testid={`${testId}-why-toggle`}
						size="compact"
						buttonVariant="hairline"
						noFullWidthMobile
						aria-expanded={whyOpen}
						aria-label={t(whyOpen ? "request.outreach.row.hide_aria" : "request.outreach.row.why_aria", { name: row.name })}
						onClick={onToggleWhy}
						sx={{ borderRadius: BADGE.PILL_RADIUS_FULL, gap: 0.25, color: "primary.dark", flexShrink: 0 }}
					>
						{t(whyOpen ? "request.outreach.row.hide" : "request.outreach.row.why")}
						<ExpandMoreRoundedIcon
							fontSize="small"
							sx={{
								transform: whyOpen ? "rotate(180deg)" : "none",
								transition: `transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
								"@media (prefers-reduced-motion: reduce)": { transition: "none" },
							}}
						/>
					</SystemButton>
				)}
			</Box>
			{scored !== null && (
				<Collapse in={whyOpen} unmountOnExit>
					<OutreachWhyChips person={scored} copy={copy} dataTestId={testId} />
				</Collapse>
			)}
		</Box>
	);
};
